/**
 * Scan-pattern generators for the dock-mission planner's scan wizard.
 *
 *   generateSerpentine(polygon, opts) — fills a polygon with a back-and-
 *     forth ("boustrophedon" / lawn-mower) sweep of waypoints, aligned to
 *     the polygon's longest edge so the sweep lines run parallel to it.
 *
 *   generateLine(a, b, opts) — points evenly spaced along a line segment.
 *
 * Both return `{ lng, lat }` pairs only — alt + leg params are owned by the
 * caller (typically the mission's preset defaults or the user's edits).
 *
 * Math: all distance-based math is done in a local meters frame centered on
 * the polygon centroid, so degrees-of-lng skew at non-equatorial latitudes
 * doesn't warp the sweep spacing. We convert IN once, do the geometry, and
 * convert OUT once.
 */

type Point = [number, number];

const M_PER_DEG_LAT = 111_000;

function mPerDegLng(latDeg: number): number {
  return M_PER_DEG_LAT * Math.cos((latDeg * Math.PI) / 180);
}

/** Cross product of vectors (b-a) and (c-a). Positive = CCW turn. */
function cross(a: Point, b: Point, c: Point): number {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
}

/** Ray-casting point-in-polygon for points in the SAME 2-D frame as the
 *  polygon (callers pass meters here, not degrees). */
function pointInPolygon2D(pt: Point, poly: Point[]): boolean {
  if (poly.length < 3) return false;
  const [x, y] = pt;
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Clip a horizontal line at y = `yLine` to the polygon — returns the
 *  segment(s) where the line lies inside the polygon. For convex polygons
 *  this is 0 or 1 segments; we handle general polygons by collecting all
 *  edge-crossing x values and pairing them in sorted order. */
function clipHorizontalToPolygon(
  poly: Point[],
  yLine: number,
): Array<[number, number]> {
  const xs: number[] = [];
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if (yi === yj) continue; // horizontal edge — degenerate at y=yLine
    if (yi > yLine === yj > yLine) continue; // both above or both below
    // Edge crosses the line — interpolate x at yLine.
    const t = (yLine - yi) / (yj - yi);
    xs.push(xi + t * (xj - xi));
  }
  xs.sort((a, b) => a - b);
  const segments: Array<[number, number]> = [];
  for (let k = 0; k + 1 < xs.length; k += 2) {
    segments.push([xs[k], xs[k + 1]]);
  }
  return segments;
}

/** Rotate a point around the origin by `angleRad`. */
function rotate(p: Point, angleRad: number): Point {
  const c = Math.cos(angleRad);
  const s = Math.sin(angleRad);
  return [p[0] * c - p[1] * s, p[0] * s + p[1] * c];
}

export interface SerpentineOptions {
  /** Distance between parallel sweep lines, in meters. Default 15 m. */
  lineSpacingM?: number;
}

/**
 * Lawn-mower / boustrophedon sweep of a polygon. The polygon is rotated so
 * its longest edge runs horizontal in the local frame, parallel sweep lines
 * are laid at `lineSpacingM` intervals across its bounding box, each is
 * clipped to the polygon, and odd lines are reversed so consecutive lines
 * snake-connect (no long jumps between sweep ends). The full path is then
 * rotated back and re-projected to lng/lat.
 *
 * Returns the generated waypoint positions in flight order. Empty when the
 * polygon has <3 vertices or no horizontal sweep lands inside it.
 */
export function generateSerpentine(
  polygon: Point[],
  opts: SerpentineOptions = {},
): Point[] {
  if (polygon.length < 3) return [];
  const lineSpacingM = opts.lineSpacingM ?? 15;
  if (lineSpacingM <= 0) return [];

  // Local meters frame centered on the polygon centroid. All sweep math
  // below operates in this frame so degree-of-lng skew at non-equatorial
  // latitudes doesn't bias the spacing.
  const cx = polygon.reduce((s, p) => s + p[0], 0) / polygon.length;
  const cy = polygon.reduce((s, p) => s + p[1], 0) / polygon.length;
  const mLng = mPerDegLng(cy);
  const polyM: Point[] = polygon.map(([x, y]) => [
    (x - cx) * mLng,
    (y - cy) * M_PER_DEG_LAT,
  ]);

  // Longest-edge bearing — we rotate the polygon so this edge runs along
  // the x-axis. Sweep lines run parallel to it, which is the orientation
  // operators expect for an inspection pattern (long passes, few turns).
  let longestSq = 0;
  let longestAngle = 0;
  for (let i = 0; i < polyM.length; i++) {
    const a = polyM[i];
    const b = polyM[(i + 1) % polyM.length];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const d2 = dx * dx + dy * dy;
    if (d2 > longestSq) {
      longestSq = d2;
      longestAngle = Math.atan2(dy, dx);
    }
  }

  // Rotate polygon by -longestAngle so the longest edge runs horizontally.
  const rot = polyM.map((p) => rotate(p, -longestAngle));

  // Bounding box in the rotated frame.
  let minY = Infinity;
  let maxY = -Infinity;
  for (const [, y] of rot) {
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }

  // Inset the first sweep by half the spacing so the path doesn't hug the
  // polygon edge exactly (the edge gets less coverage otherwise).
  const inset = lineSpacingM / 2;
  const yStart = minY + inset;
  const yEnd = maxY - inset;
  if (yEnd < yStart) return [];

  const path: Point[] = [];
  let flip = false;
  for (let y = yStart; y <= yEnd + 1e-6; y += lineSpacingM) {
    const segs = clipHorizontalToPolygon(rot, y);
    if (segs.length === 0) continue;
    // For non-convex polygons there may be multiple intersection segments;
    // use the union endpoints (leftmost in, rightmost out) for v1. Operators
    // can refine waypoints after if a concave NFZ-like notch needs special
    // handling.
    const left = Math.min(...segs.map((s) => s[0]));
    const right = Math.max(...segs.map((s) => s[1]));
    // Inset the endpoints by lineSpacing/2 along x as well so the corners
    // sit inside the polygon, not right on its boundary.
    const insetX = Math.min(inset, (right - left) / 2);
    const xL = left + insetX;
    const xR = right - insetX;
    const a: Point = flip ? [xR, y] : [xL, y];
    const b: Point = flip ? [xL, y] : [xR, y];
    path.push(a, b);
    flip = !flip;
  }
  if (path.length === 0) return [];

  // Rotate back and project to lng/lat.
  return path.map((p) => {
    const r = rotate(p, longestAngle);
    return [cx + r[0] / mLng, cy + r[1] / M_PER_DEG_LAT];
  });
}

export interface LineOptions {
  /** Distance between consecutive points, in meters. Default 10 m. */
  stepM?: number;
  /** Include endpoint at the very tail of the segment. Default true. */
  includeEnd?: boolean;
}

/**
 * Evenly-spaced points along a line segment. Starts at `a`, ends at `b`
 * (or just short of `b` when stepM doesn't divide the segment length
 * evenly). All math in a local meters frame so the spacing matches user
 * intent regardless of latitude.
 */
export function generateLine(a: Point, b: Point, opts: LineOptions = {}): Point[] {
  const stepM = opts.stepM ?? 10;
  if (stepM <= 0) return [a, b];
  const lat0 = (a[1] + b[1]) / 2;
  const mLng = mPerDegLng(lat0);
  const ax = a[0] * mLng;
  const ay = a[1] * M_PER_DEG_LAT;
  const bx = b[0] * mLng;
  const by = b[1] * M_PER_DEG_LAT;
  const dxM = bx - ax;
  const dyM = by - ay;
  const lenM = Math.hypot(dxM, dyM);
  if (lenM === 0) return [a];
  const includeEnd = opts.includeEnd !== false;
  const count = Math.max(1, Math.floor(lenM / stepM));
  const points: Point[] = [];
  for (let i = 0; i <= count; i++) {
    const t = (i * stepM) / lenM;
    if (t > 1) break;
    const x = ax + dxM * t;
    const y = ay + dyM * t;
    points.push([x / mLng, y / M_PER_DEG_LAT]);
  }
  // Always include the tail of the segment so the inspection actually ends
  // at the user's second click point (unless explicitly disabled).
  if (includeEnd) {
    const last = points[points.length - 1];
    if (last && (last[0] !== b[0] || last[1] !== b[1])) points.push(b);
  }
  return points;
}

// Suppress unused warning for pointInPolygon2D / cross — kept in the
// module surface for callers that want to manually clip more sophisticated
// scan patterns in a future iteration.
void pointInPolygon2D;
void cross;
